import { useCallback, useMemo, useRef, useState } from 'react';
import { ChatStreamMode } from '../constants/chatStreamMode';
import { t } from '../i18n/uiText';
import { jsonHeadersWithAuth } from '../utils/webAuth';
import { requestAuthPopup } from '../utils/authPopup';
import { resolveAiLimitMessage } from '../utils/aiLimitMessage';
import { telegramService } from '../services/telegram';
import {
  buildCleanSnapshot,
  buildInsightQuestionSnapshot,
  INSIGHT_TAB_MODES,
  isChatFollowUpMode,
  messageBelongsToInsightTab,
  shouldSkipRepeatVerseChip,
} from '../utils/verseInsightHelpers';

const API_BASE =
  import.meta.env.VITE_API_URL || 'https://verbum-api-production.up.railway.app/api';

const MAX_QUESTION_CHARS = 300;

function parseIntOrNull(v) {
  if (v == null || v === '') return null;
  const n = parseInt(String(v), 10);
  return Number.isFinite(n) ? n : null;
}

export function useVerseInsightChat({ bookId, chapter, verse, translation }) {
  const [messages, setMessages] = useState([]);
  const [tabSnapshots, setTabSnapshots] = useState({});
  const [loading, setLoading] = useState(false);
  const [tabErrors, setTabErrors] = useState({});
  const inFlightTabRef = useRef(null);
  const fetchIdRef = useRef(0);

  const versePayload = useMemo(
    () => ({
      verseSourceBookId: parseIntOrNull(bookId),
      verseSourceChapter: parseIntOrNull(chapter),
      verseSourceVerse: parseIntOrNull(verse),
    }),
    [bookId, chapter, verse]
  );

  const hasVerseCoords =
    versePayload.verseSourceBookId != null &&
    versePayload.verseSourceChapter != null &&
    versePayload.verseSourceVerse != null;

  const baseSnapshot = useMemo(
    () => ({
      initialQuestion: t('verseExplain'),
      userTurns: [],
      assistantTurns: [],
    }),
    []
  );

  const getTabSnapshot = useCallback(
    (tabId) => tabSnapshots[tabId] || (hasVerseCoords ? baseSnapshot : null),
    [tabSnapshots, hasVerseCoords, baseSnapshot]
  );

  const showAssistantError = useCallback((message) => {
    const errorText = String(message || t('error')).trim();
    if (!errorText) return;
    setMessages((prev) => {
      const next = [...prev];
      const last = next[next.length - 1];
      if (last?.role === 'assistant') {
        next[next.length - 1] = {
          ...last,
          text: errorText,
          streaming: false,
          isError: true,
        };
        return next;
      }
      next.push({ role: 'assistant', text: errorText, streaming: false, isError: true });
      return next;
    });
  }, []);

  const consumeChat = useCallback(
    async (payload, nextSnapshotBase = null, nextUserTurn = null, options = null) => {
      const shouldUpdateSnapshot = options?.updateSnapshot !== false;
      const fetchId = ++fetchIdRef.current;

      const response = await fetch(`${API_BASE}/chat`, {
        method: 'POST',
        headers: jsonHeadersWithAuth({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          ...payload,
          ...versePayload,
          isTelegram: telegramService.isTelegramWebApp,
          language: (localStorage.getItem('bibleLanguage') || 'en').toLowerCase(),
          translation,
        }),
      });
      const data = await response.json().catch(() => null);
      if (response.status === 401) {
        const next = `${window.location.pathname}${window.location.search}`;
        requestAuthPopup(next, true);
        throw new Error('__AUTH_REQUIRED__');
      }
      if (response.status === 429 && data?.code === 'DAILY_AI_LIMIT_REACHED') {
        throw new Error(resolveAiLimitMessage(data, t));
      }
      if (!response.ok) throw new Error(data?.error || t('error'));
      const assistantText = typeof data?.text === 'string' ? data.text : '';
      if (!assistantText.trim()) throw new Error(t('error'));
      if (fetchId !== fetchIdRef.current) return;

      setMessages((prev) => {
        const next = [...prev];
        if (next.length > 0 && next[next.length - 1].role === 'assistant') {
          next[next.length - 1] = {
            ...next[next.length - 1],
            text: assistantText.trim(),
            streaming: false,
          };
        }
        return next;
      });

      if (!shouldUpdateSnapshot) return;

      const insightTab = options?.insightTab;
      if (insightTab && nextSnapshotBase) {
        const existingUserTurns = Array.isArray(nextSnapshotBase.userTurns)
          ? nextSnapshotBase.userTurns
          : [];
        const updatedUserTurns = nextUserTurn
          ? [...existingUserTurns, nextUserTurn]
          : [...existingUserTurns];
        const nextTabSnap = {
          initialQuestion: nextSnapshotBase.initialQuestion,
          userTurns: updatedUserTurns,
          assistantTurns: [...nextSnapshotBase.assistantTurns, assistantText.trim()],
        };
        setTabSnapshots((prev) => ({ ...prev, [insightTab]: nextTabSnap }));
        return;
      }

      if (nextSnapshotBase) {
        const existingUserTurns = Array.isArray(nextSnapshotBase.userTurns)
          ? nextSnapshotBase.userTurns
          : [];
        const updatedUserTurns = nextUserTurn
          ? [...existingUserTurns, nextUserTurn]
          : [...existingUserTurns];
        setTabSnapshots((prev) => ({
          ...prev,
          explain: {
            initialQuestion: nextSnapshotBase.initialQuestion,
            userTurns: updatedUserTurns,
            assistantTurns: [...nextSnapshotBase.assistantTurns, assistantText.trim()],
          },
        }));
      } else {
        setTabSnapshots((prev) => ({
          ...prev,
          explain: {
            initialQuestion: payload.message,
            userTurns: [],
            assistantTurns: [assistantText.trim()],
          },
        }));
      }
    },
    [translation, versePayload]
  );

  const submitChip = useCallback(
    async (text, mode, { chipEmoji, updateSnapshot = false } = {}) => {
      if (!text || !mode) return;
      if (shouldSkipRepeatVerseChip(messages, mode)) return;
      if (loading) return;

      setTabErrors((prev) => {
        const next = { ...prev };
        const tabKey = Object.keys(INSIGHT_TAB_MODES).find((k) => INSIGHT_TAB_MODES[k] === mode);
        if (tabKey) delete next[tabKey];
        return next;
      });
      setLoading(true);

      const tabKey = Object.keys(INSIGHT_TAB_MODES).find((k) => INSIGHT_TAB_MODES[k] === mode);

      setMessages((prev) => [
        ...prev,
        {
          role: 'user',
          text,
          asFollowupChip: true,
          insightTab: tabKey,
          ...(chipEmoji ? { chipEmoji } : {}),
        },
        {
          role: 'assistant',
          text: '',
          streaming: true,
          modeTag: mode,
          insightTab: tabKey,
          showModeLabel: true,
        },
      ]);

      const snapForFollowUp =
        tabKey === 'simple'
          ? getTabSnapshot('explain') || (hasVerseCoords ? baseSnapshot : null)
          : getTabSnapshot(tabKey) || (hasVerseCoords ? baseSnapshot : null);
      const scopedMessages =
        tabKey === 'simple'
          ? messages.filter((m) => messageBelongsToInsightTab(m, 'explain'))
          : tabKey
            ? messages.filter((m) => messageBelongsToInsightTab(m, tabKey))
            : messages;
      const cleanSnapshotForFollowUp = snapForFollowUp
        ? buildCleanSnapshot(snapForFollowUp, scopedMessages, {
            forFollowUp: isChatFollowUpMode(mode),
          })
        : null;

      try {
        const snapBody = cleanSnapshotForFollowUp
          ? {
              initialQuestion: cleanSnapshotForFollowUp.initialQuestion,
              userTurns: cleanSnapshotForFollowUp.userTurns || [],
              assistantTurns: cleanSnapshotForFollowUp.assistantTurns,
            }
          : snapForFollowUp
            ? {
                initialQuestion: snapForFollowUp.initialQuestion,
                userTurns: snapForFollowUp.userTurns || [],
                assistantTurns: snapForFollowUp.assistantTurns,
              }
            : undefined;

        await consumeChat(
          {
            mode,
            message: text,
            ...(snapBody ? { snapshot: snapBody } : {}),
          },
          snapForFollowUp,
          text,
          {
            updateSnapshot:
              mode === ChatStreamMode.Application ? updateSnapshot !== true : false,
          }
        );
      } catch (err) {
        if (err?.message === '__AUTH_REQUIRED__') return;
        const tabKey = Object.keys(INSIGHT_TAB_MODES).find((k) => INSIGHT_TAB_MODES[k] === mode);
        if (tabKey) {
          setTabErrors((prev) => ({ ...prev, [tabKey]: err?.message || t('error') }));
        }
        showAssistantError(err?.message || t('error'));
      } finally {
        setLoading(false);
        inFlightTabRef.current = null;
      }
    },
    [messages, tabSnapshots, loading, hasVerseCoords, baseSnapshot, getTabSnapshot, consumeChat, showAssistantError]
  );

  const runFollowUp = useCallback(
    async (mode, label, chipEmoji) => {
      if (loading) return;
      if (shouldSkipRepeatVerseChip(messages, mode)) return;

      const tabKey = 'simple';
      setLoading(true);
      setMessages((prev) => [
        ...prev,
        {
          role: 'user',
          text: label,
          isFollowUp: true,
          asFollowupChip: true,
          insightTab: tabKey,
          ...(chipEmoji ? { chipEmoji } : {}),
        },
        {
          role: 'assistant',
          text: '',
          streaming: true,
          modeTag: mode,
          insightTab: tabKey,
          showModeLabel: true,
        },
      ]);

      const explainMessages = messages.filter((m) => messageBelongsToInsightTab(m, 'explain'));
      const effectiveSnapshot = getTabSnapshot('explain') || (hasVerseCoords ? baseSnapshot : null);
      try {
        const cleanSnap = effectiveSnapshot
          ? buildCleanSnapshot(effectiveSnapshot, explainMessages, { forFollowUp: true })
          : null;
        const snapBody = cleanSnap
          ? {
              initialQuestion: cleanSnap.initialQuestion,
              userTurns: cleanSnap.userTurns || [],
              assistantTurns: cleanSnap.assistantTurns,
            }
          : effectiveSnapshot
            ? {
                initialQuestion: effectiveSnapshot.initialQuestion,
                userTurns: effectiveSnapshot.userTurns || [],
                assistantTurns: effectiveSnapshot.assistantTurns,
              }
            : undefined;
        await consumeChat(
          { mode, message: label, ...(snapBody ? { snapshot: snapBody } : {}) },
          effectiveSnapshot,
          label,
          { updateSnapshot: false }
        );
      } catch (err) {
        if (err?.message === '__AUTH_REQUIRED__') return;
        setTabErrors((prev) => ({ ...prev, simple: err?.message || t('error') }));
        showAssistantError(err?.message || t('error'));
      } finally {
        setLoading(false);
        inFlightTabRef.current = null;
      }
    },
    [loading, messages, tabSnapshots, hasVerseCoords, baseSnapshot, getTabSnapshot, consumeChat, showAssistantError]
  );

  const ensureTabLoaded = useCallback(
    async (tab) => {
      if (tab === 'question') return;
      const mode = INSIGHT_TAB_MODES[tab];
      if (!mode) return;
      if (shouldSkipRepeatVerseChip(messages, mode)) return;
      if (loading && inFlightTabRef.current === tab) return;
      if (inFlightTabRef.current === tab) return;
      inFlightTabRef.current = tab;

      if (tab === 'explain') {
        await submitChip(t('verseExplain'), ChatStreamMode.VerseExplain, {
          chipEmoji: '\u{1F4D6}',
        });
        return;
      }
      if (tab === 'application') {
        await submitChip(t('chatApplyLifePrompt'), ChatStreamMode.Application, {
          chipEmoji: '\u{1F9ED}',
          updateSnapshot: false,
        });
        return;
      }
      if (tab === 'simple') {
        await runFollowUp(ChatStreamMode.Simple, t('chatFollowUpSimple'), '\u2728');
      }
    },
    [messages, loading, submitChip, runFollowUp]
  );

  const submitQuestion = useCallback(
    async (rawText, activeTab) => {
      const text = String(rawText || '').trim().slice(0, MAX_QUESTION_CHARS);
      if (!text || loading || !activeTab) return;

      const questionErrorKey = `question_${activeTab}`;
      setTabErrors((prev) => {
        const next = { ...prev };
        delete next[questionErrorKey];
        return next;
      });
      setLoading(true);

      const tabSnapshot = getTabSnapshot(activeTab);
      const questionSnapshot = buildInsightQuestionSnapshot(
        tabSnapshot,
        messages,
        activeTab,
        t('verseExplain')
      );
      setMessages((prev) => [
        ...prev,
        { role: 'user', text, insightTab: activeTab },
        {
          role: 'assistant',
          text: '',
          streaming: true,
          modeTag: ChatStreamMode.Question,
          insightTab: activeTab,
        },
      ]);

      try {
        const snapBody = {
          initialQuestion: questionSnapshot.initialQuestion,
          userTurns: questionSnapshot.userTurns || [],
          assistantTurns: questionSnapshot.assistantTurns || [],
        };
        const hasDialogContext =
          snapBody.assistantTurns.length > 0 ||
          snapBody.userTurns.length > 0 ||
          Boolean(snapBody.initialQuestion);

        if (hasVerseCoords || hasDialogContext) {
          await consumeChat(
            {
              mode: ChatStreamMode.Question,
              message: text,
              snapshot: snapBody,
            },
            questionSnapshot,
            text,
            { insightTab: activeTab }
          );
        } else {
          await consumeChat({ mode: ChatStreamMode.Question, message: text }, null, null);
        }
      } catch (err) {
        if (err?.message === '__AUTH_REQUIRED__') return;
        setTabErrors((prev) => ({ ...prev, [questionErrorKey]: err?.message || t('error') }));
        showAssistantError(err?.message || t('error'));
      } finally {
        setLoading(false);
      }
    },
    [loading, tabSnapshots, messages, hasVerseCoords, getTabSnapshot, consumeChat, showAssistantError]
  );

  const resetSession = useCallback(() => {
    fetchIdRef.current += 1;
    setMessages([]);
    setTabSnapshots({});
    setTabErrors({});
    inFlightTabRef.current = null;
    setLoading(false);
  }, []);

  return {
    messages,
    loading,
    tabErrors,
    ensureTabLoaded,
    submitQuestion,
    resetSession,
    retryTab: (tab) => {
      if (tab === 'question') return;
      const mode = INSIGHT_TAB_MODES[tab];
      if (!mode) return;
      setMessages((prev) => {
        const next = [...prev];
        while (next.length > 0) {
          const last = next[next.length - 1];
          if (last?.role === 'assistant' && last.modeTag === mode) {
            next.pop();
            break;
          }
          if (last?.role === 'user' && (last.asFollowupChip || last.isFollowUp)) {
            const peek = next[next.length - 2];
            if (peek?.role === 'assistant' && peek.modeTag === mode) {
              next.pop();
              next.pop();
              break;
            }
            next.pop();
            break;
          }
          break;
        }
        return next;
      });
      inFlightTabRef.current = null;
      void ensureTabLoaded(tab);
    },
  };
}
